// @flow strict
import * as React from 'react';
import useI18n from 'src/hooks/useI18n';
import type {JOB_INFORMATION} from 'src/types/crm.js';

import {
  BodyMedium,
  BodyMediumBold,
  BodySmall,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {
  Table,
  BasicSingleCell,
} from '@spaced-out/ui-design-system/lib/components/Table';

import {getFormattedDate} from 'src/utils/crm';
import {useInflight} from 'src/hooks/redux';

import {useSelector, useDispatch} from 'react-redux';
import {selectJobInformation, selectLocations} from 'src/selectors/crm';
import {getJobInformation} from 'src/action-creators/crm';

import css from './additional-job-information.css';


export const AdditionalInfoTab = ({jobId}: {jobId?: string}): React.Node => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const jobInfo = useSelector(selectJobInformation);
  const dispatch = useDispatch();

  React.useEffect(() => {
    if (jobId) {
      dispatch(getJobInformation(jobId));
    }
  }, [jobId]);

  const tableEntries = [
    {
      label: t('Due_date', 'Due date'),
      value: jobInfo?.dueDate ? getFormattedDate(jobInfo.dueDate) : '-',
    },
    {
      label: t('Functional_area', 'Functional area'),
      value: jobInfo?.functionalArea ?? 'NA',
      id: 'functionalArea',
    },
    {
      label: t('OrganizationLine_of_Business', 'Organization/Line of Business'),
      value: jobInfo.organizationName ?? 'NA',
      id: 'organizationName',
    },
    {
      label: t('Industry', 'Industry'),
      value: jobInfo.clientDomain ?? '-',
      id: 'clientDomain',
    },
  ].filter(Boolean);

  const BasicTableName = ({data}): React.Node => (
    <BasicSingleCell>
      <BodySmall color="secondary">{data.label}</BodySmall>
    </BasicSingleCell>
  );

  const BasicTableValue = ({data}): React.Node => (
    <BasicSingleCell>
      <BodyMediumBold>{data.value}</BodyMediumBold>
    </BasicSingleCell>
  );

  return (
    <Table
      borderRadius="12px"
      entries={tableEntries}
      headers={[
        {
          label: t('Job_Attribute', 'Job Attribute'),
          key: 'label',
          sortable: false,
          className: css.smallColumn,
          render: BasicTableName,
        },
        {
          label: t('Details', 'Details'),
          key: 'value',
          sortable: false,
          render: BasicTableValue,
        },
      ]}
    />
  );
};
